import { forwardRef, useImperativeHandle, useRef } from 'react';
import { DateTime } from 'luxon';
import DateTimePicker from '../../../Shared/DateTimePicker';
import ValidationInfo from '../../../../validation/validation-info';
import { TranslationService } from '../../../../services/translation.service';
import TextFieldFormGroup from '../../../Shared/TextFieldFormGroup';
import type IValidatable from '../../../Shared/validatable.interface';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import ValidationEngine from '../../../../validation/validation-engine';
import ActualTripSelectorSummary from './components/Summary';
import { DateHelper } from '../../../../helpers/date.helper';
import { ActualArrivalSelectorField, ActualArrivalSelectorMode } from './actual-arrival-selector.enum';

export interface IActualArrivalSelectorState {
  value: {
    mode: ActualArrivalSelectorMode;
    dateTime: DateTime;
    comments: string;
  };
  validationInfo: ValidationInfo;
}

interface IActualArrivalSelectorProps {
  plannedDepartureDateTime: DateTime | undefined;
  plannedArrivalDateTime: DateTime | undefined;
  className?: string;
  state: IActualArrivalSelectorState;
  onChange?: ((value: { mode: ActualArrivalSelectorMode; dateTime: DateTime; comments: string }) => void) | undefined;
  onKeyUp?: ((field: ActualArrivalSelectorField) => void) | undefined;
  onEditClick?: () => void;
}

export default forwardRef<IValidatable, IActualArrivalSelectorProps>(function ActualArrivalSelector(
  { plannedDepartureDateTime, plannedArrivalDateTime, className, state, onChange, onKeyUp, onEditClick }: IActualArrivalSelectorProps,
  ref
) {
  // === REFERENCES ===
  const dateTimePickerRef = useRef<IValidatable>(null!);
  const commentsRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const selectedArrivalDateTimeValidationInfo = new ValidationInfo(`${TranslationService.get('delaycompensationformpage.actualarrivaltimelessthantwentyminutestext')} - `);
      if (plannedDepartureDateTime && state.value.dateTime <= plannedDepartureDateTime) {
        // NV-4659 special case when actual arrival is before planned departure.
        ValidationEngine.for(selectedArrivalDateTimeValidationInfo)
          .isAfter(state.value.dateTime, plannedDepartureDateTime, 'delaycompensationformpage.actualarrivalisafterplanneddeparture')
          .validate();
      } else {
        // NV-4291 add 19 minutes to validate that actual arrivaltime is at least 20 minutes after planned arrival
        ValidationEngine.for(selectedArrivalDateTimeValidationInfo)
          .required(plannedArrivalDateTime)
          .required(state.value.dateTime)
          .isAfter(state.value.dateTime, plannedArrivalDateTime!.plus({ minutes: 19 }), 'delaycompensationformpage.actualarrivaltimelessthantwentyminuteserrortext')
          .isBefore(state.value.dateTime, plannedArrivalDateTime!.plus({ hours: 24 }), 'delaycompensationformpage.actualarrivalistofarfromplanned')
          .validate();
      }
      if (selectedArrivalDateTimeValidationInfo.isInvalid) {
        selectedArrivalDateTimeValidationInfo.children = {
          date: new ValidationInfo('', [''], {}),
          time: new ValidationInfo('', [''], {}),
        };
      }
      return new ValidationInfo('', [], {
        dateTime: selectedArrivalDateTimeValidationInfo,
        comments: commentsRef.current.checkValidity(),
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      const selectedArrivalDateTimeValidationInfo = dateTimePickerRef.current.handleServerValidationErrors(errors);
      if (selectedArrivalDateTimeValidationInfo.isInvalid) {
        selectedArrivalDateTimeValidationInfo.children = {
          date: new ValidationInfo('', [''], {}),
          time: new ValidationInfo('', [''], {}),
        };
      }
      return new ValidationInfo('', [], {
        dateTime: selectedArrivalDateTimeValidationInfo,
        comments: commentsRef.current.handleServerValidationErrors(errors),
      });
    },
  }));

  return (
    <div className={`actual-arrival-selector ${className ?? ''}`}>
      <div className={state.value.mode === ActualArrivalSelectorMode.edit ? '' : 'd-none'}>
        <div className="mb-3 mb-md-4">
          <div id="date-time-picker">
            <DateTimePicker
              ref={dateTimePickerRef}
              minDateTime={DateTime.now().startOf('day').minus({ days: 60 })}
              maxDateTime={DateTime.now()}
              minuteStep={1}
              showAsteriskOnRequiredFields={true}
              legend={TranslationService.get('delaycompensationformpage.whenarriveatfinalstoptext')}
              showLegend={true}
              serverValidationPrefix="actualArrival"
              state={{
                value: state.value.dateTime,
                validationInfo: state.validationInfo.children.dateTime ?? new ValidationInfo(),
              }}
              onChange={(value: DateTime) => {
                onKeyUp?.(ActualArrivalSelectorField.datetime);
                onChange?.({ ...state.value, dateTime: value });
              }}
            />
          </div>
        </div>
        <TextFieldFormGroup
          ref={commentsRef}
          required={true}
          renderAsTextArea={true}
          maxLength={2000}
          labelText={TranslationService.get('common.comments')}
          rows={5}
          inputId={'actual-arrival-selector-comments'}
          serverValidationName="actualArrivalComments"
          state={{
            value: state.value.comments,
            validationInfo: state.validationInfo.children.comments ?? new ValidationInfo(),
          }}
          onChange={(value) => {
            onChange?.({ ...state.value, comments: value });
          }}
          onKeyUp={() => {
            onKeyUp?.(ActualArrivalSelectorField.comments);
          }}
        />
      </div>
      <div className={state.value.mode === ActualArrivalSelectorMode.summary ? '' : 'd-none'}>
        <ActualTripSelectorSummary
          arrivalDateTime={state.value.dateTime}
          comments={state.value.comments}
          showOnlyTimeInSummary={plannedArrivalDateTime ? DateHelper.isSameDay(state.value.dateTime, plannedArrivalDateTime) : false}
          onEditClick={onEditClick}
        />
      </div>
    </div>
  );
});
